import { useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Popover from '@mui/material/Popover'
import FormHelperText from '@mui/material/FormHelperText'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import PendingIcon from '@mui/icons-material/Pending'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import { useTranslation } from 'react-i18next'

import { useActivityOptions, getOption } from '../../../utils'
import SponsorshipStatusTag from '../../abstracts/components/SponsorshipStatusTag'
import { Sponsorship, SponsorshipStatus, SponsorshipType } from '../../../types'

function StatusIcon({ status }: { status?: SponsorshipStatus | null }) {
  switch (status) {
    case SponsorshipStatus.Pending:
      return <TimerOutlinedIcon fontSize="small" />
    case SponsorshipStatus.Accepted:
      return <CheckBoxOutlinedIcon fontSize="small" />
    case SponsorshipStatus.WaitingList:
      return <HourglassEmptyOutlinedIcon fontSize="small" />
    case SponsorshipStatus.Rejected:
      return <NotInterestedIcon fontSize="small" />
    case SponsorshipStatus.LabSelected:
      return <PendingIcon fontSize="small" />
    default:
      return <CheckBoxOutlineBlankIcon fontSize="small" />
  }
}

export default function SponsorshipDetails({
  sponsorship,
  sponsorshipType,
}: {
  sponsorship: Sponsorship
  sponsorshipType: SponsorshipType | undefined
}) {
  const { t } = useTranslation('dashboard')

  const activityOptions = useActivityOptions(t)

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <Stack gap="0.5rem">
      <Typography
        variant="h4"
        width="fit-content"
        sx={theme => ({ borderBottom: `2px solid ${theme.palette.divider}` })}
        marginBottom="0.2rem"
      >
        {t('Demande de prise en charge')} :
      </Typography>
      <Stack direction="row" gap="1rem" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap="1rem" flex={{ sx: '0 0', lg: '1 0' }}>
          <StatusIcon status={sponsorship.status} />
          <Stack direction="column" flex="1 0 auto">
            <Typography className="MuiFormControlLabel-label">
              {t('État de la demande')} :
            </Typography>
          </Stack>
        </Stack>
        <Stack flex={{ sx: '0 0', lg: '1 0' }}>
          <SponsorshipStatusTag status={sponsorship.status} size="small" />
        </Stack>
      </Stack>
      <Stack
        direction="column"
        sx={{
          padding: '0.5rem',
          backgroundColor: theme => theme.colors.secondaryBackground,
          borderRadius: '0.5rem',
          border: theme => `0.1rem solid ${theme.colors.secondary}`,
        }}
      >
        <Stack direction="row" gap="1rem">
          <Typography className="MuiFormControlLabel-label">{t('Activité')} :</Typography>
          <Typography className="MuiFormControlLabel-label" fontWeight={600}>
            {getOption(activityOptions, null, sponsorship.activity)?.label}
          </Typography>
        </Stack>

        <FormControlLabel
          defaultChecked={sponsorship.request_accommodation_costs}
          disabled
          control={<Checkbox readOnly defaultChecked={sponsorship.request_accommodation_costs} />}
          label={t("Prise en charge des frais d'hébergement")}
          sx={{ display: sponsorshipType?.slug === 'medtech' ? 'none' : 'flex' }}
        />
        <FormControlLabel
          defaultChecked={sponsorship.request_registration_costs}
          disabled
          control={<Checkbox readOnly defaultChecked={sponsorship.request_registration_costs} />}
          label={t("Prise en charge des frais d'inscription")}
          sx={{ display: sponsorshipType?.slug === 'medtech' ? 'none' : 'flex' }}
        />

        <FormControlLabel
          defaultChecked={sponsorship.request_travel_costs}
          disabled
          control={<Checkbox readOnly defaultChecked={sponsorship.request_travel_costs} />}
          label={t('Prise en charge des frais de transport')}
        />

        <Typography
          className="MuiFormControlLabel-label"
          sx={{
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
          onClick={handleClick}
          aria-describedby={id}
        >
          {t('Conditions générales de prise en charge')}
        </Typography>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <ul style={{ margin: 0 }}>
            {sponsorshipType?.terms_and_conditions.map((term, index) => (
              <li>
                <FormHelperText key={`sponsors-condition-${index}`}>{term}</FormHelperText>
              </li>
            ))}
          </ul>
        </Popover>
      </Stack>
    </Stack>
  )
}
